import React from 'react';
import { Link } from 'react-router-dom';
import { challengeSummaryFooterClass, challengeSummarySurfaceClass, sectionLabelClass } from './challengeWireFormStyles';
import {
	buildChallengeScheduleLine,
	challengeFirstName,
	getChallengeParticipantNames
} from '../../utils/challengeSummaryDisplay';
import ChallengeWorkoutPlanButton from './ChallengeWorkoutPlanButton';
import ChallengeDayTracking from './ChallengeDayTracking';
import ChallengeTypeBadge from './ChallengeTypeBadge';
import ChallengeStatusIndicator from './ChallengeStatusIndicator';
import ChallengeMutualSupportLine from './ChallengeMutualSupportLine';

export const challengeCardBtn = {
	primary:
		'flex-1 min-w-0 rounded-lg bg-blue-600 py-2 px-3 text-sm font-medium text-white hover:bg-blue-700 active:bg-blue-800 transition',
	success:
		'flex-1 min-w-0 rounded-lg bg-green-600 py-2 px-3 text-sm font-medium text-white hover:bg-green-700 active:bg-green-800 dark:bg-green-600 dark:hover:bg-green-500 transition',
	secondary:
		'flex-1 min-w-0 rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg py-2 px-3 text-sm font-medium text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 transition',
	ghost:
		'shrink-0 rounded-lg py-2 px-3 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-dark-text transition'
};

export default function ChallengeCard({ challenge, children, onDayCheck, isDayCheckPending }) {
	const { challengerName, challengedName, isSelfChallenge } = getChallengeParticipantNames(challenge);
	const challenger = challengeFirstName(challengerName) || challengerName || 'Someone';
	const challenged = challengeFirstName(challengedName) || challengedName || 'someone';
	const scheduleLine = buildChallengeScheduleLine(challenge.starts_on, challenge.ends_on);
	const note = challenge.personal_note?.trim();

	const hasActions = React.Children.toArray(children).filter(Boolean).length > 0;

	return (
		<article
			className={`flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-dark-border ${challengeSummarySurfaceClass}`}
		>
			<div className="flex-1 p-4 sm:p-5 space-y-3">
				<div className="flex items-start justify-between gap-3">
					<div className="flex min-w-0 items-center gap-2">
						<p className={sectionLabelClass}>Challenge</p>
						<ChallengeTypeBadge challenge={challenge} isSelfChallenge={isSelfChallenge} />
					</div>
					<ChallengeStatusIndicator status={challenge.status} />
				</div>

				<div className="space-y-2 text-sm leading-relaxed text-gray-800 dark:text-dark-text">
					<p>
						{isSelfChallenge ? (
							<>
								<strong className="font-semibold text-gray-900 dark:text-dark-text">
									{challenger}
								</strong>
								{' challenges themselves to:'}
							</>
						) : (
							<>
								<strong className="font-semibold text-gray-900 dark:text-dark-text">
									{challenger}
								</strong>
								{' challenges '}
								<strong className="font-semibold text-gray-900 dark:text-dark-text">
									{challenged}
								</strong>
								{' to:'}
							</>
						)}
					</p>
					<p className="text-base font-semibold text-gray-900 dark:text-dark-text">{challenge.title}</p>
					{challenge.community_challenge_id && (
						<p className="text-sm">
							<Link
								to={`/community-challenges/${challenge.community_challenge_id}`}
								className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
							>
								View leaderboard →
							</Link>
						</p>
					)}
					<ChallengeWorkoutPlanButton challenge={challenge} className="mt-2" />
					{scheduleLine && <p>{scheduleLine}</p>}
					{note ? (
						<blockquote className="border-l-2 border-blue-300 dark:border-blue-600 pl-3 italic text-gray-600 dark:text-gray-400">
							{note}
						</blockquote>
					) : null}
					<ChallengeMutualSupportLine mutualSupport={challenge.mutual_support} />
				</div>

				<ChallengeDayTracking
					challenge={challenge}
					onDayCheck={
						onDayCheck ? (isoDate, checked) => onDayCheck(challenge, isoDate, checked) : undefined
					}
					isDayCheckPending={isDayCheckPending}
					readOnly={!onDayCheck}
				/>
			</div>

			{hasActions && (
				<footer className={`mt-auto flex flex-wrap items-center gap-2 px-4 py-3 ${challengeSummaryFooterClass}`}>
					{children}
				</footer>
			)}
		</article>
	);
}
